import { EventType } from "@/analytics/analytics-events";
import {
  OpenChatEventProperties,
  OpenHelpCenterResourceProperties,
} from "@/analytics/help-center-menu-events-properties";
import { SwitchToPreviousUiDialog } from "@/features/hb-to-sxg-migration/switch-to-previous-ui-dialog";
import {
  LATEST_WHATS_NEW_ARTICLE_KEY,
  useCheckWhatsNew,
} from "@/hooks/use-check-whats-new";
import { runtimeConfig } from "@/runtime-config";
import { useAppSelector } from "@/store/store-hooks";
import { selectIsHbToSxgMigrated } from "@/store/subscription-features/subscription-features-selectors";
import { HelpCenterMenu, codeToLanguageCodes } from "@faro-lotv/flat-ui";
import {
  Analytics,
  useIntercom,
  useLocalizeLanguage,
} from "@faro-lotv/foreign-observers";
import { useCallback, useState, type JSX } from "react";

type ViewerHelpCenterMenuProps = {
  /** Whether the users of a migrated workspace may switch back to the HoloBuilder WebEditor from here */
  allowSwitchToPreviousUi?: boolean;
};

/**
 * @returns the Faro HelpCenterMenu configured for the Sphere Viewer app
 */
export function ViewerHelpCenterMenu({
  allowSwitchToPreviousUi = false,
}: ViewerHelpCenterMenuProps): JSX.Element {
  const language = useLocalizeLanguage();

  const helpCenterLanguage = codeToLanguageCodes(language.code);

  const intercom = useIntercom();

  const { newArticle, setNewArticle } = useCheckWhatsNew();

  const isHbToSxgMigrated = useAppSelector(selectIsHbToSxgMigrated);
  const canSwitchToPreviousUi = allowSwitchToPreviousUi && isHbToSxgMigrated;
  const [isSwitchToPreviousUiOpen, setIsSwitchToPreviousUiOpen] =
    useState(false);

  const sendFeedback = useCallback(() => {
    window.open(runtimeConfig.externalLinks.feedbackForm, "blank");
  }, []);

  const switchToPreviousUi = useCallback(() => {
    Analytics.track(EventType.clickOnSwitchToPreviousUi);
    setIsSwitchToPreviousUiOpen(true);
  }, []);

  return (
    <>
      <HelpCenterMenu
        onOpenHelpCenter={() => {
          Analytics.track(EventType.openHelpMenu);
        }}
        shouldShowWhatsNewBadge={Boolean(newArticle)}
        onOpenHelpLink={(helpType) => {
          Analytics.track<OpenHelpCenterResourceProperties>(
            EventType.openHelpResource,
            {
              helpType,
            },
          );

          if (helpType === "whats-new") {
            if (newArticle) {
              localStorage.setItem(LATEST_WHATS_NEW_ARTICLE_KEY, newArticle);
            }
            setNewArticle(undefined);
          }
        }}
        openChat={
          intercom
            ? () => {
                Analytics.track<OpenChatEventProperties>(EventType.openChat, {
                  hasUnreadMessages: !!intercom.unreadMessagesCount,
                });

                intercom.showMessenger();
              }
            : undefined
        }
        numUnreadMsg={intercom ? intercom.unreadMessagesCount : undefined}
        helpCenterLanguages={helpCenterLanguage}
        // The menu hides the entries it gets no callback for
        sendFeedback={
          runtimeConfig.externalLinks.feedbackForm ? sendFeedback : undefined
        }
        switchToPreviousUi={
          canSwitchToPreviousUi ? switchToPreviousUi : undefined
        }
      />
      {canSwitchToPreviousUi && (
        <SwitchToPreviousUiDialog
          open={isSwitchToPreviousUiOpen}
          onClose={() => setIsSwitchToPreviousUiOpen(false)}
        />
      )}
    </>
  );
}
